import {useRouter, useRouterState, type ParsedLocation} from '@tanstack/react-router'
import {useSyncExternalStore} from 'react'

import {toReactRouterLocation} from './use-location'

// TanStack keeps `isLoading` true until after the destination matches render, and only then sets
// `resolvedLocation`. `onBeforeRouteMount` fires when the matches are committed, so it marks the point
// where the destination starts rendering.
const mountedLocations = new WeakMap<object, ParsedLocation>()

// Keeps the resolved source location while loading, then switches as soon as the destination matches render.
export const useRenderedLocation = () => {
  const router = useRouter()
  const mountedLocation = useSyncExternalStore(
    onChange => {
      const unsubscribeLoad = router.subscribe('onBeforeLoad', () => {
        mountedLocations.delete(router)
        onChange()
      })
      const unsubscribeMount = router.subscribe('onBeforeRouteMount', event => {
        mountedLocations.set(router, event.toLocation)
        onChange()
      })
      return () => {
        unsubscribeLoad()
        unsubscribeMount()
      }
    },
    () => mountedLocations.get(router),
    () => undefined,
  )

  return useRouterState({
    select: state => {
      if (!state.isLoading) return toReactRouterLocation(state.location)
      // Ignore a mount event for a destination that a newer navigation has replaced.
      const rendered = mountedLocation?.href === state.location.href ? mountedLocation : undefined
      return toReactRouterLocation(rendered ?? state.resolvedLocation ?? state.location)
    },
    structuralSharing: true,
  })
}
